/**
 * Admin: modul contact — denumire, activ, mesaj, telefon.
 */
import { $ } from '../utils/helpers.js?v=82.86';
import { showSnackbar } from '../utils/ui.js?v=82.86';
import { validateReservationPhoneInput, validateMenuUrl } from '../utils/validation.js?v=82.86';
import { getCurrentUser } from '../services/auth.js?v=82.86';
import { getCurrentSettings, setCurrentSettings } from '../services/settings.js?v=82.86';
import { saveCampaignSettingsBackend } from '../services/backend.js?v=82.86';
import { refreshAdminSummaries } from './admin.js?v=82.86';
import { logger } from '../utils/logger.js?v=82.86';

export const DEFAULT_CONTACT_LABEL = 'Rezervare';

export function buildContactModulePayload() {
  const reservationLabel =
    String($('reservationLabel')?.value || '')
      .trim()
      .slice(0, 40) || DEFAULT_CONTACT_LABEL;
  const enabled = $('reservationEnabled')?.checked === true;
  const phoneRaw = $('reservationPhone')?.value || '';
  const phoneValidation = phoneRaw.trim()
    ? validateReservationPhoneInput(phoneRaw)
    : { valid: true, value: '' };

  if (enabled && !phoneValidation.valid) {
    throw new Error(phoneValidation.message);
  }
  if (!enabled && phoneRaw.trim() && !phoneValidation.valid) {
    throw new Error(phoneValidation.message);
  }

  const reservationPhone = phoneValidation.valid ? phoneValidation.value : '';

  // WhatsApp: același număr (bifa) sau altul
  const waSame = $('reservationWhatsappSame')?.checked === true;
  let reservationWhatsapp = '';
  if (waSame) {
    if (!reservationPhone) throw new Error('Pentru WhatsApp „același număr”, completează întâi telefonul.');
    reservationWhatsapp = reservationPhone;
  } else {
    const waRaw = String($('reservationWhatsapp')?.value || '').trim();
    if (waRaw) {
      const v = validateReservationPhoneInput(waRaw);
      if (!v.valid) throw new Error(`WhatsApp: ${v.message}`);
      reservationWhatsapp = v.value;
    }
  }

  // Link: site, comandă online, programări
  let reservationLink = '';
  const linkRaw = String($('reservationLink')?.value || '').trim();
  if (linkRaw) {
    const withProto = /^https?:\/\//i.test(linkRaw) ? linkRaw : `https://${linkRaw}`;
    const lv = validateMenuUrl(withProto);
    if (!lv.valid || !lv.value) throw new Error(`Link: ${lv.message || 'invalid'}`);
    reservationLink = lv.value;
  }
  const reservationLinkLabel = String($('reservationLinkLabel')?.value || '').trim().slice(0, 30);

  if (enabled && !reservationPhone && !reservationWhatsapp && !reservationLink) {
    throw new Error('Completează cel puțin telefonul, WhatsApp sau un link.');
  }

  return {
    reservationLabel,
    reservationEnabled: enabled,
    reservationPhone,
    reservationMessage: String($('reservationMessage')?.value || '').trim().slice(0, 220),
    reservationWhatsapp,
    reservationLink,
    reservationLinkLabel
  };
}

const digitsOf = (x) => String(x || '').replace(/\D/g, '');
function syncWhatsappSame() {
  const same = $('reservationWhatsappSame')?.checked === true;
  const wa = $('reservationWhatsapp');
  if (!wa) return;
  wa.hidden = same;
  wa.disabled = same;
}

function bindContactModuleAdminControls() {
  const enabledEl = $('reservationEnabled');
  if (enabledEl && !enabledEl.dataset.contactToggleBound) {
    enabledEl.dataset.contactToggleBound = '1';
    enabledEl.addEventListener('change', () => refreshAdminSummaries());
  }

  const labelEl = $('reservationLabel');
  if (labelEl && !labelEl.dataset.contactLabelBound) {
    labelEl.dataset.contactLabelBound = '1';
    labelEl.addEventListener('input', () => { refreshAdminSummaries(); syncPresetChips(); });
  }

  // butoanele rapide doar completează numele (rămâne editabil)
  document.querySelectorAll('[data-contact-preset]').forEach((b) => {
    if (b.dataset.bound) return;
    b.dataset.bound = '1';
    b.addEventListener('click', () => {
      const el = $('reservationLabel');
      if (!el) return;
      el.value = b.dataset.contactPreset || '';
      el.dispatchEvent(new Event('input', { bubbles: true }));
    });
  });

  const sameEl = $('reservationWhatsappSame');
  if (sameEl && !sameEl.dataset.bound) {
    sameEl.dataset.bound = '1';
    sameEl.addEventListener('change', syncWhatsappSame);
  }
}

function syncPresetChips() {
  const v = String($('reservationLabel')?.value || '').trim().toLowerCase();
  document.querySelectorAll('[data-contact-preset]').forEach((b) => {
    const on = String(b.dataset.contactPreset || '').toLowerCase() === v;
    b.classList.toggle('is-on', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
}

export function hydrateContactModuleAdminFromSettings(settings) {
  const s = settings || getCurrentSettings() || {};
  if ($('reservationLabel')) {
    const label = String(s.reservationLabel || '').trim();
    $('reservationLabel').value = label || DEFAULT_CONTACT_LABEL;
  }
  if ($('reservationEnabled')) {
    $('reservationEnabled').checked = s.reservationEnabled === true;
  }
  if ($('reservationMessage')) {
    $('reservationMessage').value = (s.reservationMessage || '').trim();
  }
  if ($('reservationPhone')) {
    $('reservationPhone').value = (s.reservationPhone || '').trim();
  }
  const wa = String(s.reservationWhatsapp || '').trim();
  if ($('reservationWhatsappSame')) {
    $('reservationWhatsappSame').checked = !!wa && digitsOf(wa) === digitsOf(s.reservationPhone);
  }
  if ($('reservationWhatsapp')) {
    $('reservationWhatsapp').value = wa;
  }
  if ($('reservationLink')) $('reservationLink').value = String(s.reservationLink || '').trim();
  if ($('reservationLinkLabel')) $('reservationLinkLabel').value = String(s.reservationLinkLabel || '').trim();
  bindContactModuleAdminControls();
  syncWhatsappSame();
  syncPresetChips();
}

export async function saveContactModuleSettings(options = {}) {
  const { quiet = false } = options;
  const currentUser = getCurrentUser();
  if (!currentUser) throw new Error('Nu ești logat');
  const uid = currentUser.uid;

  const toSave = buildContactModulePayload();
  await saveCampaignSettingsBackend(toSave);

  const currentSettings = getCurrentSettings();
  if (currentSettings) {
    currentSettings.reservationLabel = toSave.reservationLabel;
    currentSettings.reservationEnabled = toSave.reservationEnabled;
    currentSettings.reservationPhone = toSave.reservationPhone;
    currentSettings.reservationMessage = toSave.reservationMessage;
    currentSettings.reservationWhatsapp = toSave.reservationWhatsapp;
    currentSettings.reservationLink = toSave.reservationLink;
    currentSettings.reservationLinkLabel = toSave.reservationLinkLabel;
    setCurrentSettings(currentSettings);
  }

  refreshAdminSummaries();

  const { applyClientUI } = await import('./slideshow.js?v=82.86');
  applyClientUI();

  if (!quiet) {
    showSnackbar('Modul contact salvat.', 'success');
  }

  setTimeout(() => {
    import('../services/settings.js?v=82.86')
      .then((m) => m.loadSettings(uid, true))
      .catch((err) => {
        logger.warn('[saveContactModuleSettings] Background reload failed:', err);
      });
  }, 3000);
}

/** Alias compatibilitate */
export const saveReservationSettings = saveContactModuleSettings;
